import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, X } from 'lucide-react';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { EmptyState } from './shared';
import {
  computeHeroBgStyle,
  computeHeroOverlayStyle,
  DEFAULT_HERO_IMAGE,
  getHeroSubtitleStyle,
  renderColoredTitle,
} from '@/features/home/heroCmsUtils';

export const DesignSection = ({ siteContent, setSiteContent, onSave, saving }) => {
  if (!siteContent) return <EmptyState message="Kunde inte ladda innehåll" />;

  const previewHero = siteContent.hero || {};
  const previewTitleText = (previewHero.title || '').trim() || 'BYGGVAROR TILL FYNDPRIS – VARJE DAG';
  const previewSubtitleText = (previewHero.subtitle || '').trim() || 'Underrubrik visas här …';
  const previewBg = previewHero.background_image || DEFAULT_HERO_IMAGE;
  const previewBgStyle = computeHeroBgStyle(previewBg, previewHero.background_align_x, previewHero.background_align_y);
  const previewOverlayStyle = computeHeroOverlayStyle(previewHero.brightness);
  const previewSubtitleStyle = getHeroSubtitleStyle(previewHero.subtitle_color);

  const addColoredWord = () => {
    const currentColors = siteContent.hero?.title_colors || [];
    setSiteContent({
      ...siteContent,
      hero: {
        ...siteContent.hero,
        title_colors: [...currentColors, { word: '', color: 'orange' }]
      }
    });
  };

  const updateColoredWord = (index, field, value) => {
    const currentColors = [...(siteContent.hero?.title_colors || [])];
    currentColors[index] = { ...currentColors[index], [field]: value };
    setSiteContent({
      ...siteContent,
      hero: { ...siteContent.hero, title_colors: currentColors }
    });
  };

  const removeColoredWord = (index) => {
    const currentColors = (siteContent.hero?.title_colors || []).filter((_, i) => i !== index);
    setSiteContent({
      ...siteContent,
      hero: { ...siteContent.hero, title_colors: currentColors }
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-design">
      {/* Hero Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="font-bold text-[#0F172A] mb-4">Startsidans hero</h3>
        <div className="space-y-4">
          <div>
            <Label>Rubrik</Label>
            <Input
              value={siteContent.hero?.title || ''}
              onChange={(e) => setSiteContent({ ...siteContent, hero: { ...siteContent.hero, title: e.target.value } })}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Färgade ord i rubriken</Label>
            <p className="text-sm text-gray-500 mb-2">Skriv in ord som ska ha annan färg</p>
            {(siteContent.hero?.title_colors || []).map((item, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input
                  placeholder="Ord"
                  value={item.word}
                  onChange={(e) => updateColoredWord(index, 'word', e.target.value)}
                  className="flex-1"
                />
                <Select value={item.color} onValueChange={(v) => updateColoredWord(index, 'color', v)}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="orange">Orange</SelectItem>
                    <SelectItem value="blue">Blå</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => removeColoredWord(index)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addColoredWord}>+ Lägg till färgat ord</Button>
          </div>

          <div>
            <Label>Underrubrik</Label>
            <Textarea
              value={siteContent.hero?.subtitle || ''}
              onChange={(e) => setSiteContent({ ...siteContent, hero: { ...siteContent.hero, subtitle: e.target.value } })}
              className="mt-1"
              rows={2}
            />
            <div className="mt-3">
              <Label>Underrubrik — textfärg</Label>
              <p className="text-sm text-gray-500 mt-1 mb-2">Standard matchar den mjuka vita tonen på startsidan.</p>
              <Select
                value={siteContent.hero?.subtitle_color || 'default'}
                onValueChange={(v) =>
                  setSiteContent({
                    ...siteContent,
                    hero: { ...siteContent.hero, subtitle_color: v },
                  })
                }
              >
                <SelectTrigger className="max-w-xs mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Standard (mjuk vit)</SelectItem>
                  <SelectItem value="white">Vit</SelectItem>
                  <SelectItem value="orange">Orange</SelectItem>
                  <SelectItem value="blue">Blå (ByggFynd)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Bakgrundsbild</Label>
            <p className="text-sm text-gray-500 mt-1 mb-2">
              Ladda upp en bild (Cloudinary) eller klistra in en bild-URL nedan.
            </p>
            <ImageUpload
              folder="banners"
              aspectRatio="aspect-[21/9]"
              uploadLabel="Ladda upp hero-bakgrund"
              currentImage={siteContent.hero?.background_image || null}
              onUpload={(url) =>
                setSiteContent({
                  ...siteContent,
                  hero: { ...siteContent.hero, background_image: url || '' },
                })
              }
              className="mt-2 max-w-2xl"
            />
            <Label className="mt-4 block">Bild-URL (alternativ eller efter uppladdning)</Label>
            <Input
              value={siteContent.hero?.background_image || ''}
              onChange={(e) =>
                setSiteContent({
                  ...siteContent,
                  hero: { ...siteContent.hero, background_image: e.target.value },
                })
              }
              className="mt-1 font-mono text-sm"
              placeholder="https://..."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <Label>Horisontell placering</Label>
                <p className="text-sm text-gray-500 mt-1 mb-2">
                  Var ska bilden fästas vid beskärning (cover).
                </p>
                <Select
                  value={siteContent.hero?.background_align_x || 'center'}
                  onValueChange={(v) =>
                    setSiteContent({
                      ...siteContent,
                      hero: { ...siteContent.hero, background_align_x: v },
                    })
                  }
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Vänster</SelectItem>
                    <SelectItem value="center">Mitten</SelectItem>
                    <SelectItem value="right">Höger</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vertikal placering</Label>
                <p className="text-sm text-gray-500 mt-1 mb-2">
                  Välj <strong>Topp</strong> om viktigt innehåll (t.ex. logotyp) ligger högt i bilden och klipps bort.
                </p>
                <Select
                  value={siteContent.hero?.background_align_y || 'center'}
                  onValueChange={(v) =>
                    setSiteContent({
                      ...siteContent,
                      hero: { ...siteContent.hero, background_align_y: v },
                    })
                  }
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">Topp</SelectItem>
                    <SelectItem value="center">Mitten</SelectItem>
                    <SelectItem value="bottom">Botten</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div>
            <Label>Ljusstyrka: {siteContent.hero?.brightness ?? 10}%</Label>
            <p className="text-sm text-gray-500 mt-1">
              Högre värde ger ljusare hero (svagare mörk toning över bilden). Glöm inte att spara ändringar.
            </p>
            <input
              type="range" min="0" max="100"
              value={siteContent.hero?.brightness ?? 10}
              onChange={(e) => setSiteContent({ ...siteContent, hero: { ...siteContent.hero, brightness: parseInt(e.target.value, 10) } })}
              className="w-full mt-2"
            />
          </div>

          <div
            className="mt-6 rounded-xl border border-slate-200 overflow-hidden bg-slate-100 shadow-inner"
            data-testid="hero-preview"
          >
            <div className="px-4 py-2.5 border-b border-slate-200 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <span className="text-sm font-semibold text-[#0F172A]">Förhandsgranskning — startsidans hero</span>
              <span className="text-xs text-slate-500">Uppdateras direkt · Spara längst ned för att publicera</span>
            </div>
            <div className="relative min-h-[260px] md:min-h-[300px] flex items-center justify-center p-5 md:p-8">
              <div className="absolute inset-0 z-0" style={previewBgStyle} />
              <div className="absolute inset-0 z-[1] pointer-events-none" style={previewOverlayStyle} />
              <div className="relative z-10 w-full max-w-2xl mx-auto text-center px-2">
                <h1 className="hero-title !text-[clamp(1.15rem,3.5vw,2.15rem)] !leading-tight !mb-2">
                  {renderColoredTitle(previewTitleText, siteContent.hero?.title_colors)}
                </h1>
                <p
                  className="hero-subtitle !text-[clamp(0.85rem,1.8vw,1.05rem)] !mb-4 !max-w-xl"
                  style={previewSubtitleStyle}
                >
                  {previewSubtitleText}
                </p>
                <div
                  className="search-premium max-w-lg mx-auto pointer-events-none select-none scale-[0.92] origin-top"
                  aria-hidden
                >
                  <Search className="h-4 w-4 text-gray-400 ml-3 hidden sm:block shrink-0" />
                  <span className="flex-1 text-left pl-2 sm:pl-3 text-sm text-gray-400 truncate py-3">
                    Sök efter produkter, kategorier eller butiker…
                  </span>
                  <span className="search-btn-premium text-sm px-4 py-2.5">
                    <Search className="h-4 w-4" />
                    <span className="hidden md:inline">Sök</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-footer Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="font-bold text-[#0F172A] mb-4">Sektion ovanför footer</h3>
        <div className="space-y-4">
          <div>
            <Label>Rubrik</Label>
            <Input
              value={siteContent.pre_footer?.title || ''}
              onChange={(e) => setSiteContent({ ...siteContent, pre_footer: { ...siteContent.pre_footer, title: e.target.value } })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Underrubrik</Label>
            <Input
              value={siteContent.pre_footer?.subtitle || ''}
              onChange={(e) => setSiteContent({ ...siteContent, pre_footer: { ...siteContent.pre_footer, subtitle: e.target.value } })}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Bakgrundsfärg</Label>
              <Input
                type="color"
                value={siteContent.pre_footer?.background_color || '#003B73'}
                onChange={(e) => setSiteContent({ ...siteContent, pre_footer: { ...siteContent.pre_footer, background_color: e.target.value } })}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <Label>Textfärg</Label>
              <Input
                type="color"
                value={siteContent.pre_footer?.text_color || '#FFFFFF'}
                onChange={(e) => setSiteContent({ ...siteContent, pre_footer: { ...siteContent.pre_footer, text_color: e.target.value } })}
                className="mt-1 h-10"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Knapptext</Label>
              <Input
                value={siteContent.pre_footer?.button_text || ''}
                onChange={(e) => setSiteContent({ ...siteContent, pre_footer: { ...siteContent.pre_footer, button_text: e.target.value } })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Knapplänk</Label>
              <Input
                value={siteContent.pre_footer?.button_link || ''}
                onChange={(e) => setSiteContent({ ...siteContent, pre_footer: { ...siteContent.pre_footer, button_link: e.target.value } })}
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </div>

      <Button className="bg-[#003B73]" onClick={onSave} disabled={saving}>
        {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        Spara ändringar
      </Button>
    </div>
  );
};
